/* global Promise */

import {ElementUtil} from '../utils/elementUtil.js';
import {StringUtil} from '../utils/stringUtil.js';
import {SessionStorageUtil} from '../utils/browserStorageSanitizer.js';

export class BaseService {
	
	constructor() {		
	}
	
	async performFetch(url, fetchOptions) {
		fetchOptions = fetchOptions || {};
		
		var workingCompanyId = SessionStorageUtil.getItem('workingCompany');
		if (workingCompanyId) {
			if (!fetchOptions.headers) {
				fetchOptions.headers = {};
			}
			fetchOptions.headers['Company'] = workingCompanyId;
		}
		
		if (!fetchOptions.headers) {
			fetchOptions.headers = {};
		}
		//We need to set this so that basic auth prompt in the browser
		fetchOptions.headers['X-Requested-With'] = 'XMLHttpRequest';
		
		try {
			var res = await fetch(url, fetchOptions);
			return res;
		} catch (err) {
			//timeout network error
			window.DL.MessageUtil.showConfirm({
				title: 'Network Communication Failure',
				message: 'No response from Server or Connection Timeout. <br> Check connection and refresh page.',
				buttons: [
					{
						text: 'Reload',
						theme: 'primary',
						handler: function() {
							window.location.reload();
						}						
					}
				]
			});
			return Promise.reject(err);
		}			
	}
	
	async makeGETRequest(url, textResponse, fetchOptions) {	
		var res;
		if (fetchOptions) {
			res = await this.performFetch(url, fetchOptions);
		} else {
			res = await this.performFetch(url);
		}
		if (res.ok) {
			if (textResponse) {
				var text = await res.text();
				return text;
			} else {
				var data = await res.json();
				return data;				
			}
		} else {
			this.handleResponseError(res, {
				url: url,
				method: 'GET'
			});
			var error = new Error("Communication Failure");
			error.res  = res;
			return Promise.reject(error);
		}
	}
	
	async makePOSTRequest(url, data, options) {
		const res = await this.performFetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(data)
		});
		if (res.ok) {
			var data = await res.json();
			if (!options || options.displayValidationErrors !== false) {
				this.displayValidationErrors(data);
			}
			return data;
		} else {
			this.handleResponseError(res, {
				url: url,
				method: 'POST',
				data: data
			});
			var error = new Error("Communication Failure");
			error.res  = res;
			return Promise.reject(error);
		}
	}
	
	async makePOSTRequestText(url, data) {
		const res = await this.performFetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'text/plain'
			},
			body: data
		});
		if (res.ok) {
			var data = await res.json();
			this.displayValidationErrors(data);
			return data;
		} else {
			this.handleResponseError(res, {
				url: url,
				method: 'POST',
				data: data
			});
			var error = new Error("Communication Failure");
			error.res  = res;
			return Promise.reject(error);
		}
	}	
	
	async makePUTRequest(url, data) {
		const res = await this.performFetch(url, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(data)
		});
		if (res.ok) {
			var data = await res.json();
			this.displayValidationErrors(data);
			return data;
		} else {
			this.handleResponseError(res, {
				url: url,
				method: 'PUT',
				data: data
			});
			var error = new Error("Communication Failure");
			error.res  = res;
			return Promise.reject(error);
		}
	}	
		
	async makeDELETERequest(url, data) {		
		var res;
		
		if (data) {
			res = await this.performFetch(url, {
				method: 'DELETE',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(data)
			});
		} else {
			res = await this.performFetch(url, {
				method: 'DELETE'
			});
		}
		
		if (res.ok) {
			const contentType = res.headers.get('content-type');
			if (contentType && contentType.indexOf('application/json') >= 0) {
				var responseData = await res.json();
				this.displayValidationErrors(responseData);
				return responseData;
			}
			return { success: true };
		} else if (res.status === 304) {
			//On 304 server should reponse with a message
			return await res.json();
		} else {
			this.handleResponseError(res, {
				url: url,
				method: 'DELETE'
			});
			var error = new Error("Communication Failure");
			error.res  = res;
			return Promise.reject(error);
		}
	}	
	
	toQueryParam(obj) {
		var params = [];
		for (var key in obj) {
			if (obj[key] || obj[key] === 0 || obj[key] === false) {
				params.push(encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]));
			}
		}		
		return params.join('&');
	}	
	
	downloadFile(url, openNewWindow) {
		var workingCompanyId = SessionStorageUtil.getItem('workingCompany');
		
		if (workingCompanyId) {
			this.downloadFileWithCompanyHeader(url);
		} else {
			if (openNewWindow) {			
				window.open(url);
			} else {
				window.location.href = url;
			}
		}
	}

	/**
	 * Downloads via fetch so the Company header is sent when working as another company.
	 */
	async downloadFileWithCompanyHeader(url) {
		window.DL.MessageUtil.showToast('Download', 'Preparing download&hellip;', {
			timeout: 15000,
			theme: 'primary'
		});

		try {
			var response = await this.performFetch(url);
			if (!response.ok) {
				window.DL.MessageUtil.showToastError('Download failed (' + response.status + ')');
				return;
			}

			var filename = 'download';
			var disposition = response.headers.get('Content-Disposition');
			if (disposition) {
				var filenameMatch = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
				if (filenameMatch && filenameMatch[1]) {
					filename = decodeURIComponent(filenameMatch[1].replace(/"/g, ''));
				}
			}

			var blob = await response.blob();
			var urlBlob = window.URL.createObjectURL(blob);
			var anchor = document.createElement('a');
			anchor.href = urlBlob;
			anchor.download = filename;
			document.body.appendChild(anchor);
			anchor.click();
			anchor.remove();
			if (URL.revokeObjectURL) {
				URL.revokeObjectURL(urlBlob);
			}
		} catch (err) {
			window.DL.MessageUtil.showToastError('Download failed. Please try again.');
		}
	}
	
	displayValidationErrors(responseData) {
		if (responseData &&			
			responseData.success === false)
		{
			if (responseData.errors) {
				var errorMessages = '';
				for (var field in responseData.errors) {
					errorMessages += '<li>' 
							+ StringUtil.camelcaseToWord(field) 
							+ ': ' 
							+ responseData.errors[field] 
							+ '</li><br>';
				}			
				
				window.DL.MessageUtil.showToastError('Check the following:<br><ul>' + errorMessages +'</ul>', 'Validation Errors');
			}
		}
	}
	
	handleResponseError(response, request) {
			
		//occurs on timeout or loss connection?
		if (response.status === 0) {
			console.log(response.status + ' check connection?');
		}
				
		var feedbackHandler = function(details){
			var requestMessage = '';			
			
			if (request) {
				requestMessage = '( URL: ' + request.url + ' | ';
			     requestMessage += '  Method: ' + request.method + ' | ';
				if (request.data) {
					try {
						requestMessage += '  Data: ' + JSON.stringify(request.data);
					} catch (e) {						
					}
				}
			}
			
			if (details) {
				requestMessage += ' \n '  + details;
			}
						
			var feedbackWin = ElementUtil.create({
				tag: 'dl-dialog',
				title: 'Report Issue',
				icon: 'vaadin:envelope',
				listeners: {
					ready: function() {
						
						feedbackWin.show();
						
						var feedbackForm = ElementUtil.create({
							tag: 'dl-feedback-form',
							slot: 'primary',
							type: 'Application Issue',
							'hideType': true,
							'descriptionPlaceHolder': 'Please provide details as to what you are trying to accomplish',
							summary: 'Application Issue',
							'extraDetail': 'Request: ' + requestMessage,
							listeners: {
								success: function() {
									window.DL.MessageUtil.showToast('Success', 'Submitted Feedback', {									
										theme: 'success'
									});
									feedbackWin.close();
								},
								cancel: function() {
									feedbackWin.close();
								}
							}
						});
						
						var flexLayout = ElementUtil.create({
							tag: 'dl-flex-layout',
							children: [								
								feedbackForm
							]
						});
						
						feedbackWin.addToContentElement(flexLayout);	
					}
				}
			});
		};		
		
		
		var requestUrl = '';
		if (response.request) {
			requestUrl = '<br><hr> ('+ request.url + ' <b>' + request.method + '</b>)'; 
		}		
		
		if (response.status === 400) {
			window.DL.MessageUtil.showConfirm({
				title: 'Bad Client Request (400)',
				message: '<i class = "fas fa-exclamation-triangle fa-2x warning-icon"></i> Check request. ' + requestUrl,
				buttons: [
					{
						text: 'Ok',
						theme: 'primary'
					},
					{
						text: 'Report Issue',
						handler: function() {
							feedbackHandler();
						}				
					}
				]
			});						
				
		} else if (response.status === 401) {
			window.DL.showLogin(function(){
				window.location.reload(true);
			});
		} else if (response.status === 403) {
			window.DL.MessageUtil.showConfirm({
				title: 'Forbidden (403)',
				message: 'Check request.  User may not have access or the request is invalid.' + requestUrl,
				buttons: [
					{
						text: 'Ok',
						theme: 'primary'
					},
					{
						text: 'Report Issue',
						handler: function() {
							feedbackHandler();
						}				
					}
				]
			});						
		} else if (response.status === 404) {			
			window.DL.MessageUtil.showToastError('Refresh and try again.  Also, check request.' + requestUrl, 'Resource Not Found (404)');			
		} else if (response.status === 405) {
			window.DL.MessageUtil.showConfirm({
				title: 'Bad Client Request (405)',
				message: 'HTTP method is not allowed.  Check request.' + requestUrl,
				buttons: [
					{
						text: 'Ok',
						theme: 'primary'
					},
					{
						text: 'Report Issue',
						handler: function() {
							feedbackHandler();
						}				
					}
				]
			});		
		} else if (response.status === 409) {
			var message = "Resource conflict. Check data.";
			
			try {
				response.json().then(function (data) {
					if (data.error) {
						for (var key in data.error) {
							message += data.error[key];
						}
						message += "<br /><br />";
					}
					window.DL.MessageUtil.showConfirm({
						title: 'Conflict (409)',
						message: message,
						buttons: [
							{
								text: 'Ok',
								theme: 'primary'
							},
							{
								text: 'Report Issue',
								handler: function () {
									feedbackHandler();
								}
							}
						]
					});
				});
			} catch (e) {
				window.DL.MessageUtil.showConfirm({
					title: 'Conflict (409)',
					message: message,
					buttons: [
						{
							text: 'Ok',
							theme: 'primary'
						},
						{
							text: 'Report Issue',
							handler: function() {
								feedbackHandler();
							}				
						}
					]
				});
			}	
				
												
		} else if (response.status === 415) {
			window.DL.MessageUtil.showConfirm({
				title: 'Unsupported Content Type (415)',
				message: 'Unsupported content type on the request.  Check request.' + requestUrl,
				buttons: [
					{
						text: 'Ok',
						theme: 'primary'
					},
					{
						text: 'Report Issue',
						handler: function() {
							feedbackHandler();
						}				
					}
				]
			});								
		} else if (response.status === 502) {			
			window.DL.MessageUtil.showToastError('This is likely a temporary condition.  Please try again later.<br>(Attention Admin): Application server is not responding to proxy. <br> Check connection and status of the Application Server. ', 'Server Communication Failure (502)');
		} else if (response.status === 503) {
			window.DL.MessageUtil.showToastError('This is likely a temporary condition.  Please try again later.<br>(Attention Admin): Application server is not responding to proxy. <br> Check connection and status of the Application Server. ', 'Server Communication Failure (502)');			
		} else if (response.status === 504) {
			window.DL.MessageUtil.showToastError('This is likely a temporary condition.  Please try again later.<br>Application is unavaliable. ', 'Gateway Timeout (504)');			
		} else if (String(response.status).charAt(0) === 5) {
			window.DL.MessageUtil.showToastError('This is likely a temporary condition.  Please try again later.<br>Application is unavaliable. ', '5xx Server Error: ' + response.status);			
		} else {			
			try {
				response.json().then(function(data) {
					var message = 'Server was unable to process request.';
					if (data.message) {
						message = data.message;
					}
					if (data.potentialResolution) {
						message = message + '<br> Potential Resolution: ' + data.potentialResolution;
					}
					if (data.errorTicketNumber) {
						message = message + '<br> Error Ticket: ' + data.errorTicketNumber;
					}

					window.DL.MessageUtil.showConfirm({
						title: 'Server Error',
						message: message,
						buttons: [
							{
								text: 'Ok',
								theme: 'primary'
							},
							{
								text: 'Report Issue',
								handler: function() {
									feedbackHandler();
								}				
							}
						]
					});
				})
				.catch(error=>{
					window.DL.MessageUtil.showToastError('Unable to connect to server or there was an internal server error.' + requestUrl, 'Connection Error');
				});
			} catch (e){
				window.DL.MessageUtil.showToastError('Unable to connect to server or there was an internal server error.' + requestUrl, 'Connection Error');				
			}
		}
	}
	
};


